<script>
import Multiselect from 'vue-multiselect'
import { debounce } from '@/mixin/helper'
import { getVSMSRestClient } from '@/services/vsms'

const vsmsAxios = getVSMSRestClient()

export default {
  components: {
    Multiselect,
  },

  props: {
    value: {
      type: Array,
      default: () => [],
    },
    deviceTypes: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      modal: {
        activeTab: 0,
        deviceType: null,
        search: null,
        input: '',
        items: [],
        selected: [],
        selectedModel: {},
        allSelected: false,
        indeterminate: false,
        currentPage: 1,
        perPage: 10,
        totalRows: 0,
        isLoading: false,
      },
    }
  },

  computed: {
    fields() {
      return [
        {
          key: 'columnCheck',
          label: '',
          thStyle: { width: '48px' },
        },
        {
          key: 'imei',
          label: 'IMEI',
          sortable: true,
        },
        {
          key: 'deviceType',
          label: 'Tipe device',
          sortable: true,
        },
        {
          key: 'simcard',
          label: 'Simcard',
          sortable: true,
        },
      ]
    },

    displayedImeis() {
      return this.value.slice(0, 2)
    },

    hiddenImeiCount() {
      return Math.max(this.value.length - this.displayedImeis.length, 0)
    },
  },

  methods: {
    openModal() {
      this.modal.activeTab = 0
      this.modal.input = this.value.join(', ')
      this.modal.selected = [...this.value]
      this.syncSelection()
      this.$bvModal.show('modal-imei-filter')
      this.getOptions()
    },

    normalizeImeis(imeis) {
      return [
        ...new Set(
          (imeis || [])
            .map((imei) => String(imei).trim())
            .filter((imei) => imei !== '')
        ),
      ]
    },

    parseImeiInput(input) {
      return this.normalizeImeis(String(input || '').split(','))
    },

    mapVehicleStatusToOption(item) {
      const vehicle = item.vehicle || {}
      const device = vehicle.device || {}

      return {
        imei: vehicle.imei,
        deviceType: device.device_name || device.deviceName || '-',
        simcard: device.phone_no || device.phoneNo || '-',
      }
    },

    async getOptions() {
      this.modal.isLoading = true
      const params = {
        page: this.modal.currentPage,
        limit: this.modal.perPage,
        search: this.modal.search,
        sorts: 'vehicle_id desc',
      }

      const filters = []
      const deviceCode = this.modal.deviceType?.deviceCode
      if (deviceCode) filters.push(`device_code eq ${deviceCode}`)
      if (filters.length) params.filters = filters.join(';')

      const res = await vsmsAxios.get('/vehicle-status', { params }).catch((error) => {
        this.$emit('error', error)
        this.modal.isLoading = false
      })

      if (!res) return

      this.modal.items = (res.data.data.paginated_result || [])
        .map(this.mapVehicleStatusToOption)
        .filter((item) => item.imei)
      this.modal.totalRows = res.data.metadata.total_count
      this.modal.isLoading = false
      this.syncSelection()
    },

    syncSelection() {
      const selectedImeis = new Set(this.modal.selected)
      this.modal.selectedModel = {}
      this.modal.items.forEach((item) => {
        this.$set(this.modal.selectedModel, item.imei, selectedImeis.has(item.imei))
      })
      this.updateSelectState()
    },

    updateSelectState() {
      const itemImeis = this.modal.items.map((item) => item.imei)
      const selectedCount = itemImeis.filter((imei) => this.modal.selected.includes(imei)).length

      this.modal.allSelected = itemImeis.length > 0 && selectedCount === itemImeis.length
      this.modal.indeterminate = selectedCount > 0 && selectedCount < itemImeis.length
    },

    toggleAll(checked) {
      const currentImeis = this.modal.items.map((item) => item.imei)
      if (checked) {
        this.modal.selected = this.normalizeImeis([...this.modal.selected, ...currentImeis])
      } else {
        this.modal.selected = this.modal.selected.filter((imei) => !currentImeis.includes(imei))
      }

      this.syncSelection()
    },

    checkImei(checked, imei) {
      if (checked) {
        this.modal.selected = this.normalizeImeis([...this.modal.selected, imei])
      } else {
        this.modal.selected = this.modal.selected.filter((item) => item !== imei)
      }

      this.$set(this.modal.selectedModel, imei, checked)
      this.updateSelectState()
    },

    searchOptions: debounce(function () {
      this.modal.currentPage = 1
      this.getOptions()
    }, 600),

    onDeviceTypeChange() {
      this.modal.currentPage = 1
      this.getOptions()
    },

    changePage(newPage) {
      this.modal.currentPage = newPage
      this.getOptions()
    },

    resetOptions() {
      this.modal.search = null
      this.modal.deviceType = null
      this.modal.currentPage = 1
      this.clearFilter()
      this.getOptions()
    },

    applyFilter() {
      const selectedImeis = this.modal.activeTab === 0
        ? this.modal.selected
        : this.parseImeiInput(this.modal.input)

      this.$emit('input', this.normalizeImeis(selectedImeis))
      this.$bvModal.hide('modal-imei-filter')
    },

    clearFilter() {
      this.modal.input = ''
      this.modal.selected = []
      this.$emit('input', [])
    },

    removeFilter(imei) {
      this.$emit('input', this.value.filter((item) => item !== imei))
    },

    customLabelDevice(item) {
      if (item.deviceName) return `${item.deviceName}`
    },
  },
}
</script>

<template>
  <div class="d-inline-flex flex-wrap justify-content-end align-items-center gap-2">
    <b-button
      variant="outline-primary"
      class="d-inline-flex align-items-center mb-2 mb-md-0"
      @click="openModal"
    >
      <i class="mc-fa icon-outline_filter me-2"></i>
      Filter IMEI
    </b-button>
    <span
      v-for="imei in displayedImeis"
      :key="imei"
      class="badge bg-primary text-white mc-imei-filter__chip px-2 py-2 mb-2 mb-md-0"
    >
      {{ imei }}
      <i class="bx bx-x" @click.stop="removeFilter(imei)"></i>
    </span>
    <span v-if="hiddenImeiCount" class="badge bg-success text-white px-2 py-2 mb-2 mb-md-0">
      + {{ hiddenImeiCount }}
    </span>
    <b-button
      v-if="value.length"
      variant="link"
      class="p-0 mb-2 mb-md-0"
      @click="clearFilter"
    >
      Bersihkan
    </b-button>

    <b-modal
      class="mc-modal"
      id="modal-imei-filter"
      title="Pilih IMEI"
      size="lg"
      centered
      scrollable
      header-bg-variant="primary"
      body-class="p-0"
    >
      <b-tabs
        v-model="modal.activeTab"
        nav-class="px-4 pt-3"
        content-class="p-4 border-top text-muted"
      >
        <b-tab title="Filter IMEI" active>
          <div>
            <b-row class="align-items-center mb-4 gy-2">
              <b-col cols="12" md="5">
                <multiselect
                  v-model="modal.deviceType"
                  :options="deviceTypes"
                  label="deviceName"
                  placeholder="Pilih Tipe Device"
                  :allow-empty="true"
                  :custom-label="customLabelDevice"
                  @input="onDeviceTypeChange"
                />
              </b-col>
              <b-col cols="12" md="7" class="text-md-end">
                <b-button variant="outline-primary" @click="resetOptions">
                  Setel Ulang
                </b-button>
              </b-col>
            </b-row>

            <div class="d-flex justify-content-end mb-3">
              <label class="d-inline-flex align-items-center mc-table-view__search">
                <i class="mc-fa fas fa-search"></i>
                <b-form-input
                  v-model="modal.search"
                  type="search"
                  placeholder="Cari..."
                  class="ms-2 form-control"
                  @input="searchOptions"
                />
              </label>
            </div>

            <div class="table-responsive mb-3 border border-1 rounded mc-table-overflow">
              <b-table
                class="datatables mb-0"
                :items="modal.items"
                :fields="fields"
                show-empty
                :empty-text="$t('global.emptyTable')"
                :busy="modal.isLoading"
              >
                <template #table-busy>
                  <div class="text-center text-danger my-2">
                    <b-spinner class="align-middle"></b-spinner>
                  </div>
                </template>
                <template #head(columnCheck)>
                  <div class="form-check" @click.stop>
                    <b-form-checkbox
                      v-model="modal.allSelected"
                      :indeterminate="modal.indeterminate"
                      :value="true"
                      :unchecked-value="false"
                      :disabled="modal.items.length === 0 || modal.isLoading"
                      @change="toggleAll"
                    />
                  </div>
                </template>
                <template #cell(columnCheck)="data">
                  <div class="form-check" @click.stop>
                    <b-form-checkbox
                      :checked="modal.selectedModel[data.item.imei]"
                      :value="true"
                      :unchecked-value="false"
                      :disabled="modal.isLoading"
                      @change="checkImei($event, data.item.imei)"
                    />
                  </div>
                </template>
                <template #cell(imei)="data">
                  <span class="text-primary fw-bold">{{ data.item.imei }}</span>
                </template>
              </b-table>
            </div>

            <div class="row align-items-center gy-2">
              <div class="col-sm-12 col-md-6">
                <span class="text-muted">
                  Menampilkan <strong>{{ modal.totalRows }}</strong> data.
                </span>
              </div>
              <div class="col-sm-12 col-md-6">
                <div class="dataTables_paginate paging_simple_numbers float-end">
                  <b-pagination
                    v-model="modal.currentPage"
                    :total-rows="modal.totalRows"
                    :per-page="modal.perPage"
                    :disabled="modal.isLoading"
                    @change="changePage"
                  />
                </div>
              </div>
            </div>
          </div>
        </b-tab>
        <b-tab title="Input IMEI">
          <div>
            <b-form-textarea
              v-model="modal.input"
              placeholder="Ketik IMEI"
              rows="12"
              class="mc-imei-filter__textarea mb-2"
            />
            <div class="d-flex justify-content-start gap-2 mt-2">
              <i class="icon-outline_info float-end pt-1"></i>
              <h6 class="pt-1 text-muted mb-0">
                Gunakan separator koma (,) untuk input IMEI lebih dari 1
              </h6>
            </div>
          </div>
        </b-tab>
      </b-tabs>

      <template #modal-footer="{ cancel }">
        <b-row class="mc-modal__filter-footer w-100">
          <b-col sm="12" md="8" class="mc-modal__filter-footer-fav"> </b-col>
          <b-col sm="12" md="4" class="mc-modal__filter-footer-btn">
            <b-button variant="outline-primary" @click="cancel" class="me-3"> Batal </b-button>
            <b-button variant="primary" :disabled="modal.isLoading" @click="applyFilter">
              {{ modal.activeTab === 0 ? 'Terapkan' : 'Simpan' }}
            </b-button>
          </b-col>
        </b-row>
      </template>
    </b-modal>
  </div>
</template>

<style scoped>
.mc-imei-filter__chip {
  align-items: center;
  display: inline-flex;
  gap: 6px;
}

.mc-imei-filter__textarea {
  min-height: 260px;
}
</style>
